import type { DecimalValue } from "../../contracts/src/portfolio-v2/index.js";

const SCALE_DIGITS = 8;
const SCALE = 100_000_000n;
const DECIMAL_PATTERN = /^-?(?:0|[1-9]\d*)(?:\.\d{1,8})?$/u;
const STABLE_FINGERPRINT_PREFIX = "sha256:v1:";
const LEGACY_FINGERPRINT_PREFIX = "fnv1a64:";
const SHA256_ROUND_CONSTANTS = Uint32Array.from([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

export class DecimalInputError extends Error {
  readonly code = "INVALID_DECIMAL";

  constructor(readonly input: unknown) {
    super("INVALID_DECIMAL");
  }
}

export function scaled(value: DecimalValue): bigint {
  if (typeof value !== "string" || !DECIMAL_PATTERN.test(value)) throw new DecimalInputError(value);
  const negative = value.startsWith("-");
  const unsigned = negative ? value.slice(1) : value;
  const [whole, fraction = ""] = unsigned.split(".");
  const result = BigInt(whole) * SCALE + BigInt(fraction.padEnd(SCALE_DIGITS, "0"));
  return negative ? -result : result;
}

export function decimal(value: bigint): DecimalValue {
  const negative = value < 0n;
  const absolute = negative ? -value : value;
  const whole = absolute / SCALE;
  const fraction = (absolute % SCALE).toString().padStart(SCALE_DIGITS, "0").replace(/0+$/u, "");
  return `${negative ? "-" : ""}${whole}${fraction ? `.${fraction}` : ""}`;
}

export function canonical(value: DecimalValue): DecimalValue {
  return decimal(scaled(value));
}

export function add(...values: DecimalValue[]): DecimalValue {
  return decimal(values.reduce((total, value) => total + scaled(value), 0n));
}

export function subtract(left: DecimalValue, right: DecimalValue): DecimalValue {
  return decimal(scaled(left) - scaled(right));
}

export function multiply(left: DecimalValue, right: DecimalValue): DecimalValue {
  return decimal(divideRounded(scaled(left) * scaled(right), SCALE));
}

export function divide(left: DecimalValue, right: DecimalValue): DecimalValue {
  const divisor = scaled(right);
  if (divisor === 0n) throw new DecimalInputError(right);
  return decimal(divideRounded(scaled(left) * SCALE, divisor));
}

export function divideFloor(left: DecimalValue, right: DecimalValue, decimalPlaces = 0): DecimalValue {
  const numerator = scaled(left);
  const denominator = scaled(right);
  if (denominator <= 0n || numerator < 0n || decimalPlaces < 0 || decimalPlaces > SCALE_DIGITS) {
    throw new DecimalInputError(`${left}/${right}@${decimalPlaces}`);
  }
  const unitFactor = 10n ** BigInt(decimalPlaces);
  const units = numerator * unitFactor / denominator;
  return decimal(units * SCALE / unitFactor);
}

export function minimum(...values: DecimalValue[]): DecimalValue {
  if (values.length === 0) throw new DecimalInputError("minimum-empty");
  return decimal(values.map(scaled).reduce((left, right) => left < right ? left : right));
}

export function maximum(...values: DecimalValue[]): DecimalValue {
  if (values.length === 0) throw new DecimalInputError("maximum-empty");
  return decimal(values.map(scaled).reduce((left, right) => left > right ? left : right));
}

export function absolute(value: DecimalValue): DecimalValue {
  const parsed = scaled(value);
  return decimal(parsed < 0n ? -parsed : parsed);
}

export function compare(left: DecimalValue, right: DecimalValue): -1 | 0 | 1 {
  const difference = scaled(left) - scaled(right);
  return difference < 0n ? -1 : difference > 0n ? 1 : 0;
}

export function isPositive(value: DecimalValue): boolean {
  return scaled(value) > 0n;
}

export function isNonNegative(value: DecimalValue): boolean {
  return scaled(value) >= 0n;
}

export function clamp(value: DecimalValue, low: DecimalValue, high: DecimalValue): DecimalValue {
  return maximum(low, minimum(value, high));
}

export function formatFixed(value: DecimalValue, places = 2): string {
  if (!Number.isInteger(places) || places < 0 || places > SCALE_DIGITS) throw new DecimalInputError(places);
  const parsed = scaled(value);
  const negative = parsed < 0n;
  const absoluteValue = negative ? -parsed : parsed;
  const divisor = 10n ** BigInt(SCALE_DIGITS - places);
  const rounded = divideRounded(absoluteValue, divisor);
  const base = 10n ** BigInt(places);
  const whole = rounded / base;
  const fraction = places === 0 ? "" : `.${(rounded % base).toString().padStart(places, "0")}`;
  return `${negative ? "-" : ""}${whole}${fraction}`;
}

export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`).join(",")}}`;
}

export function stableFingerprint(value: unknown): string {
  const text = stableStringify(value);
  return `${STABLE_FINGERPRINT_PREFIX}${sha256Hex(text)}`;
}

/**
 * Accepts historical FNV-1a fingerprints only while reading records created
 * before sha256:v1. New records must always use stableFingerprint().
 */
export function stableFingerprintMatches(value: unknown, storedFingerprint: string): boolean {
  const text = stableStringify(value);
  if (storedFingerprint.startsWith(STABLE_FINGERPRINT_PREFIX)) {
    return storedFingerprint === `${STABLE_FINGERPRINT_PREFIX}${sha256Hex(text)}`;
  }
  if (storedFingerprint.startsWith(LEGACY_FINGERPRINT_PREFIX)) {
    return storedFingerprint === legacyFNV1a64(text);
  }
  return false;
}

function legacyFNV1a64(text: string): string {
  let hash = 0xcbf29ce484222325n;
  for (const character of text) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * 0x100000001b3n);
  }
  return `${LEGACY_FINGERPRINT_PREFIX}${hash.toString(16).padStart(16, "0")}`;
}

function sha256Hex(text: string): string {
  const message = new TextEncoder().encode(text);
  const paddedLength = Math.ceil((message.length + 9) / 64) * 64;
  const padded = new Uint8Array(paddedLength);
  padded.set(message);
  padded[message.length] = 0x80;
  const bitLength = BigInt(message.length) * 8n;
  for (let index = 0; index < 8; index += 1) {
    padded[padded.length - 1 - index] = Number((bitLength >> BigInt(index * 8)) & 0xffn);
  }

  const state = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const schedule = new Uint32Array(64);

  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let index = 0; index < 16; index += 1) {
      const cursor = offset + index * 4;
      schedule[index] = (
        padded[cursor] * 0x1000000
        + padded[cursor + 1] * 0x10000
        + padded[cursor + 2] * 0x100
        + padded[cursor + 3]
      ) >>> 0;
    }
    for (let index = 16; index < 64; index += 1) {
      const word15 = schedule[index - 15];
      const word2 = schedule[index - 2];
      const sigma0 = rotateRight(word15, 7) ^ rotateRight(word15, 18) ^ (word15 >>> 3);
      const sigma1 = rotateRight(word2, 17) ^ rotateRight(word2, 19) ^ (word2 >>> 10);
      schedule[index] = (schedule[index - 16] + sigma0 + schedule[index - 7] + sigma1) >>> 0;
    }

    let a = state[0];
    let b = state[1];
    let c = state[2];
    let d = state[3];
    let e = state[4];
    let f = state[5];
    let g = state[6];
    let h = state[7];

    for (let index = 0; index < 64; index += 1) {
      const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choose = (e & f) ^ (~e & g);
      const temporary1 = (h + sum1 + choose + SHA256_ROUND_CONSTANTS[index] + schedule[index]) >>> 0;
      const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temporary2 = (sum0 + majority) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + temporary1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temporary1 + temporary2) >>> 0;
    }

    state[0] = (state[0] + a) >>> 0;
    state[1] = (state[1] + b) >>> 0;
    state[2] = (state[2] + c) >>> 0;
    state[3] = (state[3] + d) >>> 0;
    state[4] = (state[4] + e) >>> 0;
    state[5] = (state[5] + f) >>> 0;
    state[6] = (state[6] + g) >>> 0;
    state[7] = (state[7] + h) >>> 0;
  }

  return [...state].map((word) => word.toString(16).padStart(8, "0")).join("");
}

function rotateRight(value: number, bits: number): number {
  return (value >>> bits) | (value << (32 - bits));
}

function divideRounded(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) throw new DecimalInputError("division-by-zero");
  const negative = (numerator < 0n) !== (denominator < 0n);
  const absoluteNumerator = numerator < 0n ? -numerator : numerator;
  const absoluteDenominator = denominator < 0n ? -denominator : denominator;
  const quotient = absoluteNumerator / absoluteDenominator;
  const remainder = absoluteNumerator % absoluteDenominator;
  const rounded = remainder * 2n >= absoluteDenominator ? quotient + 1n : quotient;
  return negative ? -rounded : rounded;
}
